import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FNFResponse } from '../models/response';
import { IntegrationService } from './integration.service';
import { SquareIntegrationComponent } from '../components/pages/hardware/modal/square-integration/square-integration.component';

/**
 * Forces a Square resync when the backend flags this restaurant's last
 * sync/reauth as stale relative to a shipped backend change (FOR-241).
 *
 * Reuses the same dialog as a first-time Square connect — opening it always
 * restarts the flow from the top, which is exactly what a resync needs — but
 * with `forceResync` set so the dialog can't be cancelled out of.
 */
@Injectable({ providedIn: 'root' })
export class SquareResyncService {
  private dialog = inject(MatDialog);
  private integrationService = inject(IntegrationService);

  /** Guards against a duplicate stacked dialog: login.component.ts and
   * sidebar.component.ts both call checkAndPrompt() on the same fresh
   * login (login navigates to /main, which mounts the sidebar), so without
   * this a restaurant needing a resync got the check run — and the dialog
   * opened — twice, independently. Cleared once that round trip settles:
   * false on a completed check that didn't need a prompt, and again once
   * the opened dialog closes. */
  private checkInFlightOrDialogOpen = false;

  /** No-op for anything but a Square restaurant, or while an earlier call
   * is already checking or already has the dialog open. Fails silently on
   * error: a missed check just means the prompt is skipped this time, not
   * an error shown for a background check the user never asked for. */
  checkAndPrompt(restaurant: any): void {
    if (restaurant?.pos_provider !== 'square' || this.checkInFlightOrDialogOpen) {
      return;
    }
    this.checkInFlightOrDialogOpen = true;
    this.integrationService.getSquareConnectionStatus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (response?.data?.needs_resync) {
          this.promptResync();
        } else {
          this.checkInFlightOrDialogOpen = false;
        }
      },
      error: () => {
        this.checkInFlightOrDialogOpen = false;
      },
    });
  }

  private promptResync(): void {
    this.integrationService.getSquareAuthorization().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response?.data) {
          this.checkInFlightOrDialogOpen = false;
          return;
        }
        const dialogRef = this.dialog.open(SquareIntegrationComponent, {
          data: { squareAuthDetails: response.data, forceResync: true },
          width: '450px',
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe(() => {
          this.checkInFlightOrDialogOpen = false;
        });
      },
      error: () => {
        this.checkInFlightOrDialogOpen = false;
      },
    });
  }
}
